import {Injectable} from '@angular/core';
import {ActivePublicationService} from '@app/services/active-publication.service';
import {AuthService} from '@app/services/auth/auth.service';
import {UserContext} from '@models/user';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {GetCommunityList} from '@store/actions/projects.actions';
import {AppState} from '@store/app.states';
import {EMPTY, of} from 'rxjs';
import {catchError, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {RefreshList as RefreshExtProfilList} from '../../../projects/ext-profils/store/ext-profils.actions';
import {PublicationService} from '../../publications.service';
import {PublicationIntegrityService} from './services/publication-integrity.service';
import {getActivePublicationState} from '../../store/publications/publications.selector';
import * as uuid from 'uuid';
import {
  ActivePublicationActionTypes,
  ChangeProfiling, ChangeProfilingError,
  ChangeProfilingSuccess,
  ChangeQuotaRules,
  ChangeQuotaUpdateStatsFailure,
  ChangeQuotaUpdateStatsSuccess,
  GeneralInfoChangeProjectSelection,
  LoadPublication,
  LoadPublicationFailure,
  LoadPublicationSuccess,
  NewPublication, NewPublicationFromSurvey, NewPublicationFromSurveySuccess,
  NewPublicationSuccess, PUBLISH_PUB_SUCCESS,
  RefreshSurveysListForPublication,
  RefreshSurveysListForPublicationFailure,
  RefreshSurveysListForPublicationSuccess,
  ReviewSyncCintCommunity,
  ReviewSyncCintCommunityFailure,
  ReviewSyncCintCommunitySuccess,
  SavePublication,
  SavePublicationFailure,
  SavePublicationSuccess, STOP_PUB_SUCCESS,
  SurveyChooserMonetizationUpdateSurvey,
  SurveyChooserMonetizationUpdateSurveySuccess,
} from './active-publication.actions';
import {MetaSurveysInfosService} from '@app/services/metas/meta-surveys-infos.service';
import {initialState} from '@app/components/publications/create-publication/_store/active-publication.reducers';
import {ActivePublicationData} from '@models/active-publication.model';
import {confirm} from 'devextreme/ui/dialog';
import {TranslateService} from '@ngx-translate/core';
import {favoriteCommunity} from '../../../../../@shared/inbe-communities-selector/inbe-communities-selector.component';
import {Router} from '@angular/router';
import {appVersion} from '@environments/version';
import {ISurvey} from '@app/services/metas/meta-surveys-infos.objects';
import {SurveyVersionPublicationService} from '@app/components/projects/surveys/builder/survey-builder/save-version/survey-version-publication.service';
import {IDefaultCommunities, PublicationFromSurveyService} from '@app/components/publications/create-publication/publication-from-survey.service';
import {QuotaBuilderService} from '@app/components/publications/publication-dashboard-v2/quota-builder/quota-builder.service';


@Injectable()
export class ActivePublicationEffects {
  constructor(
    private actions: Actions,
    public router: Router,
    private store: Store<AppState>,
    private authService: AuthService,
    private activePublicationService: ActivePublicationService,
    private publicationaService: PublicationService,
    private publicationIntegrityService: PublicationIntegrityService,
    private metaSurveysInfosService: MetaSurveysInfosService,
    private translate: TranslateService,
    private publicationFromSurveyService: PublicationFromSurveyService,
    private quotaBuilderService: QuotaBuilderService,
  ) {
  }


  savePublication = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.SAVE_PUBLICATION),
    map((action: SavePublication) => action.payload),
    switchMap((payload) => {
      return this.activePublicationService.savePublication(payload.publication).pipe(
        map((data) => new SavePublicationSuccess({nbRows: data.data.nbRows, needRefresh: payload.needRefresh, pubUuid: payload.publication.uuid})),
        catchError((error) => of(new SavePublicationFailure({error}))),
      );
    }),
  ));

  savePublicationSuccess$ = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.SAVE_PUBLICATION_SUCCESS),
    map((action: SavePublicationSuccess) => action.payload),
    tap((payload) => {
      if (payload.needRefresh && payload.pubUuid) {
        return this.router.navigate(['publications/edit', payload.pubUuid]);
      }
    }),
  ), {dispatch: false});


  publishPub = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.PUBLISH_PUB),
    withLatestFrom(this.store.select(getActivePublicationState)),
    switchMap(([payload, state]) => {
      return this.activePublicationService.savePublication(state.datas).pipe(
        map((data) => new SavePublicationSuccess({nbRows: data.data.nbRows})),
        map((data) => new PUBLISH_PUB_SUCCESS()),
        catchError((error) => of(new SavePublicationFailure({error}))),
      );
    }),
  ));

  stopPublishingPub = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.STOP_PUB),
    withLatestFrom(this.store.select(getActivePublicationState)),
    switchMap(([payload, state]) => {
      return this.activePublicationService.savePublication(state.datas).pipe(
        map((data) => new SavePublicationSuccess({nbRows: data.data.nbRows})),
        map((data) => new STOP_PUB_SUCCESS()),
        catchError((error) => of(new SavePublicationFailure({error}))),
      );
    }),
  ));


  loadPublication = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.LOAD_PUBLICATION),
    map((action: LoadPublication) => action.payload),
    switchMap((payload) => {
      return this.activePublicationService.loadPublication(payload.uuid).pipe(
        map((data) => new LoadPublicationSuccess({publication: data.data.publication})),
        catchError((error) => of(new LoadPublicationFailure({error}))),
      );
    }),
  ));

  // @Effect()
  // publicationCreation = this.actions.pipe(
  //   ofType(ActivePublicationActionTypes.NEW_PUBLICATION),
  //   map((action: NewPublication) => action.payload),
  //   withLatestFrom(this._store.select(getActivePublicationState)),
  //   tap(([payload, state]) => {
  //
  //     const userContext: UserContext = this.authService.getContext();
  //     // this._store.dispatch(new NewPublicationSuccess({defaultOrganizationId: userContext.organizationId, defaultProjectId: userContext.currentProjectId}));
  //     return EMPTY;
  //
  //   })
  // );


  newPublication = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.NEW_PUBLICATION),
    map((action: NewPublication) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    tap(([payload, state]) => {
      const userContext: UserContext = this.authService.getContext();
      this.store.dispatch(
        // !
        new NewPublicationSuccess({
          defaultOrganizationId: userContext.organizationId,
          defaultProjectId: userContext.currentProjectId,
        }),
      );
      return EMPTY;
    }),
  ), {dispatch: false});

  newPublicationFromSurvey = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.NEW_PUBLICATION_FROM_SURVEY),
    map((action: NewPublicationFromSurvey) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    tap(([payload, state]) => {

      const userContext: UserContext = this.authService.getContext();

      return confirm(
        this.translate.instant('Ajouter les communautés [standards] de Inbe'),
        this.translate.instant('Communautés standards ?'))
        .then((result) => {

          const defaultCommunities: IDefaultCommunities = {
            organizations: result ? [146] : [],
            projects: result ? [109, 128] : [],
            communitiesIds: result ? favoriteCommunity : [],
          };

          const newPublicationData = this
            .publicationFromSurveyService.getNewPublicationFromSurvey(
              payload.surveyId,
              defaultCommunities,
              false,
            ).then(newPublicationDatas => {
              this.store.dispatch(new NewPublicationFromSurveySuccess({newPublicationDatas}));
            });

          // let addStandardCommunities = [];
          // let addInbeOrganization = [];
          // let addInbeProjects = [];
          //
          // if (result) {
          //   addStandardCommunities = favoriteCommunity;
          //   addInbeOrganization = [146];
          //   addInbeProjects = [109, 128];
          // }
          //
          // // console.log('[payload.surveyId]', [payload.surveyId]);
          // this.metaSurveysInfosService.loadSurveysInfos([payload.surveyId]).subscribe((data) => {
          //
          //   const extProfils = data.data.datas.result.extProfils;
          //
          //   const surveyInfo: ISurvey = data.data.datas.result.metas[payload.surveyId];
          //
          //   const newPublicationDatas: ActivePublicationData = {
          //     ...initialState.datas,
          //     uuid: uuid.v4(),
          //     generalInfo: {
          //       ...initialState.datas.generalInfo,
          //       hasV2Survey: surveyInfo.surveyDef.version === appVersion.surveyDef.version,
          //       title: `[${surveyInfo.id}] ${surveyInfo.organizationTitle} - ${surveyInfo.projectTitle} - ${surveyInfo.title}`,
          //       contributors: {
          //         ...initialState.datas.generalInfo.contributors,
          //         inbe: true,
          //       },
          //       selectedOrganizations: [surveyInfo.organizationId, ...addInbeOrganization],
          //       selectedProjects: [surveyInfo.projectId, ...addInbeProjects],
          //       selectedCommunities: [surveyInfo.defaultCommunityId, ...addStandardCommunities],
          //       allSelectedCommunities: [surveyInfo.defaultCommunityId, ...addStandardCommunities],
          //     },
          //     surveys: {
          //       ...initialState.datas.surveys,
          //       ids: [parseInt(surveyInfo.id, 0)],
          //     },
          //     profiling: {
          //       ...initialState.datas.profiling,
          //       selectedProfiles: [...new Set([
          //         ...initialState.datas.profiling.selectedProfiles,
          //         ...extProfils,
          //       ])],
          //     },
          //     contributorInteractions: {
          //       ...initialState.datas.contributorInteractions,
          //       extProfilsSelected: [...new Set([
          //         // ...extProfils,
          //         // 'etat_province_usa_canada',
          //         'internal_gender',
          //         // 'internal_birthdate',
          //         // 'composition_menage',
          //         // 'quel_est_le_revenu_annuel_de_votre_menage',
          //       ])],
          //     },
          //     surveyConditions: {
          //       groups: [
          //         {
          //           SQLRules: {
          //             whereClauses: [
          //               '(\n                internal_gender\n                IN\n                (\'F\', \'M\') OR internal_gender  LIKE \'%F%\' OR internal_gender  LIKE \'%M%\' )'
          //             ],
          //             fields: [
          //               'internal_gender'
          //             ],
          //             sqlRule: '(\n               internal_gender\n                IN\n                (\'F\', \'M\') OR internal_gender  LIKE \'%F%\' OR internal_gender  LIKE \'%M%\' )',
          //             cssHelp: {
          //               mapRuleDepth: [0],
          //               mapRuleFlag: ['normal']
          //             }
          //           },
          //           rules: {
          //             condition: 'and',
          //             rules: [
          //               {
          //                 label: 'internal_gender',
          //                 field: 'internal_gender',
          //                 type: 'string',
          //                 operator: 'in',
          //                 value: [
          //                   'F',
          //                   'M'
          //                 ]
          //               }
          //             ]
          //           },
          //           groupTitle: this.translate.instant('Groupe par défaut (toujours vrai)'),
          //           totalRespondent: 0,
          //           attribuableRespondent: 0,
          //           totalResult: 0,
          //           id: uuid.v4(),
          //           displayMode: 'Horizontal',
          //           isLoading: true,
          //           contentToDisplay: {
          //             surveys: null,
          //             messages: null
          //           }
          //         }
          //       ],
          //       isValid: true
          //     },
          //     publicationMessages: [
          //       {
          //         id: uuid.v4(),
          //         title: 'ScreenOut',
          //         content: {
          //           fr: 'Cher visiteur, malheureusement nous n\'avons aucune validation correspondant à votre profil pour l\'instant.',
          //           en: 'Dear visitor, unfortunately we don\'t have any validation corresponding to your profile for the moment.'
          //         },
          //         default: {
          //           screenOut: true,
          //           noMore: false,
          //           welcome: false,
          //           landing: false,
          //         }
          //       },
          //       {
          //         id: uuid.v4(),
          //         title: 'Plus de questionnaire',
          //         content: {
          //           fr: 'Merci pour votre participation et à bientôt.',
          //           en: 'Thank you for your participation and see you soon.'
          //         },
          //         default: {
          //           screenOut: false,
          //           noMore: true,
          //           welcome: false,
          //           landing: false,
          //         }
          //       }
          //     ]
          //
          //
          //   };
          //
          //   this.store.dispatch(new NewPublicationFromSurveySuccess({newPublicationDatas}));
          //
          // });


        });


      //
      // Récupération des infos du survey sélectionné
      //

    }),
  ), {dispatch: false});

  loadPublicationSuccess = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.LOAD_PUBLICATION_SUCCESS,
      ActivePublicationActionTypes.NEW_PUBLICATION_SUCCESS,
      ActivePublicationActionTypes.NEW_PUBLICATION_FROM_SURVEY_SUCCESS),
    map((action: LoadPublicationSuccess) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    tap(([payload, state]) => {

      this.store.dispatch(new RefreshExtProfilList({forceRefresh: false}));
      this.store.dispatch(new RefreshSurveysListForPublication({forceRefresh: true}));

      this.store.dispatch(new GetCommunityList({forceRefresh: false}));
    }),
  ), {dispatch: false});

  newPublicationFromSurveySuccess$ = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.NEW_PUBLICATION_FROM_SURVEY_SUCCESS),
    map((action: NewPublicationFromSurveySuccess) => action.payload),
    tap((payload) => {
      return this.store.dispatch(new SavePublication({publication: payload.newPublicationDatas, needRefresh: true}));
    }),
  ), {dispatch: false});

  //
  // On rafraîchis la liste des surevys possible quand on change de choix de projets #1
  //

  refreshSurveysListFromProjectFromGeneralInfoChange = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.GENERAL_INFO_CHANGE_PROJECT_SELECTION),
    map((action: GeneralInfoChangeProjectSelection) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    map((state) => state),
    switchMap(([payload, state]) => {
      if (payload.selectedProjects && payload.selectedProjects.length > 0) {
        return this.publicationaService.getSurveysListFromProjectIds(payload.selectedProjects).pipe(
          map(
            (data) =>
              new RefreshSurveysListForPublicationSuccess({
                list: data.data.list,
                secretToken: data.data.secretToken,
              }),
          ),
          catchError((error) => of(new RefreshSurveysListForPublicationFailure({error}))),
        );
      } else {
        return of(
          new RefreshSurveysListForPublicationSuccess({
            list: [],
            secretToken: '',
          }),
        );
      }
    }),
  ));

  //
  // Et #2 sur l'appel de l'action refresh...
  //

  refreshList = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.REFRESH_SURVEYS_LIST_FOR_PUBLICATION),
    map((action: RefreshSurveysListForPublication) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    map((state) => state),
    switchMap(([payload, state]) => {

      if (state?.datas?.generalInfo?.selectedProjects.length > 0) {

        if (state.uxInteractions.surveys.surveysList.length > 0 && !payload.forceRefresh) {

          return of(
            new RefreshSurveysListForPublicationSuccess({
              list: state.uxInteractions.surveys.surveysList,
              secretToken: state.uxInteractions.surveys.secretToken,
            }),
          );

        } else {

          return this.publicationaService.getSurveysListFromProjectIds(state.datas.generalInfo.selectedProjects).pipe(
            map(
              (data) =>
                new RefreshSurveysListForPublicationSuccess({
                  list: data.data.list,
                  secretToken: data.data.secretToken,
                }),
            ),
            catchError((error) => of(new RefreshSurveysListForPublicationFailure({error}))),
          );
        }
      } else {
        return of(
          new RefreshSurveysListForPublicationSuccess({
            list: [],
            secretToken: '',
          }),
        );
      }
    }),
  ));


  SyncCintCommunity = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.REVIEW_SYNC_CINT_COMMUNITY),

    map((action: ReviewSyncCintCommunity) => action.payload),
    switchMap((payload) => {

      return this.activePublicationService.syncCintCommunity(
        payload.action,
        payload.cintTokenComplete,
        payload.cintTokenScreenOut,
        payload.cintProjectId
      ).pipe(
        map(
          (data) =>
            new ReviewSyncCintCommunitySuccess({
              action: data.data.action,
              datas: data.data.datas,
            }),
        ),
        catchError((error) => of(new ReviewSyncCintCommunityFailure({error}))),
      );
    }),
  ));


  CalculateQuotas = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.CHANGE_QUOTA_RULES, ActivePublicationActionTypes.CHANGE_QUOTA_TOTAL_RESPONDENT_REQUIRED, ActivePublicationActionTypes.CHANGE_QUOTA_UPDATE_STATS, ActivePublicationActionTypes.CHANGE_QUOTA_QUERY_TITLE),
    map((action: ChangeQuotaRules) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    map((state) => state),
    switchMap(([payload, state]) => {
      return this.activePublicationService.getQuotasStats(state.datas.profiling.selectedProfiles, state.datas.surveys.ids, state.datas.quota.groups).pipe(
        map(
          (data) =>
            new ChangeQuotaUpdateStatsSuccess({
              stats: data.data.stats.rows,
              sqlDebug: data.data.stats.sqlDebug,
            }),
        ),
        tap((val) => console.log('[6] - quota: ', val)),
        catchError((error) => of(new ChangeQuotaUpdateStatsFailure({error}))),
      );
    }),
  ));


  UpdateSurveyMonetization = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.SURVEYS_CH_MONETIZATION_UPDATE_SURVEY),
    map((action: SurveyChooserMonetizationUpdateSurvey) => action.payload),
    switchMap((payload) => {
      return this.activePublicationService.updateSurveyMonetization(payload.survey).pipe(
        map((r) => new SurveyChooserMonetizationUpdateSurveySuccess({data: r.data})),
        catchError((error) => of(new ChangeQuotaUpdateStatsFailure({error}))),
      );
    }),
  ));

  //
  // Tests d'intégrité en cas de changement de ext profils
  //

  extProfilsIntegrityTest = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.CHANGE_PROFILING),
    map((action: ChangeProfiling) => action.payload),

    withLatestFrom(this.store.select(getActivePublicationState)),
    map((state) => state),

    switchMap(([payload, state]) => {

      return this.publicationIntegrityService.extProfilsIntegrityTest(payload, state).pipe(
        map((data: string[]) => new ChangeProfilingSuccess(data)),
        catchError((error) => // of(new ChangeProfilingError({error}))
          {
            console.log(error);
            return of(new ChangeProfilingError({error}));
          }
        ),
      );

    }),
  ));

  //
  // Recharge les ext-profiles dans le quota-builder après un changement de profiling
  //
  refreshQuotaBuilderExtProfiles = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.CHANGE_PROFILING_SUCCESS),
    map((action: ChangeProfilingSuccess) => action.payload),
    withLatestFrom(this.store.select(getActivePublicationState)),
    tap(([payload, state]) => {
      // Recharge les ext-profiles dans le quota-builder si actif
      if (this.quotaBuilderService.pubUuid && state.datas?.uuid) {
        console.log('[ActivePublicationEffects] Refreshing quota-builder ext-profiles after profiling change');
        this.quotaBuilderService.refreshExtProfiles(payload);
      }
    }),
  ), {dispatch: false});

}
